import fs from 'fs';
import { generateJsonLines } from './util.js';
import Debug from '../../helpers/debug.js';
import descriptions from '../../helpers/descriptions.js';
import crypto from 'crypto';
import { printJSON } from '../../helpers/print.js';
const debug = Debug('commands:singer:validate');

const command = 'validate';

const desc = 'Validate Singer data';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('dataFilePath', { ...descriptions.options['dataFilePath'].config })
    .option('allowDuplicateRecords', {
      type: 'boolean',
      default: false,
      description: 'Allow duplicate records',
    })
    .option('allowFuzzyTypes', {
      type: 'boolean',
      default: false,
      description: 'Allow fields to have multiple non-null types',
    });
};

const validateSchemaMatching = (streamName, newSchema, currentSchema) => {
  for (const [field, fieldSchema] of Object.entries(newSchema.properties)) {
    if (currentSchema.properties[field]) {
      const currentTyping = currentSchema.properties[field].type;
      const newTyping = fieldSchema.type;
      if (currentTyping.length != newTyping.length || currentTyping.some((t) => !newTyping.includes(t))) {
        throw new Error(
          `Schema for ${streamName} has different typing for field ${field}: ${currentTyping} and ${newTyping}`
        );
      }
    }
  }
};

const validateSchemaFormat = (streamName, schema, allowFuzzyTypes) => {
  for (const [field, fieldSchema] of Object.entries(schema.properties)) {
    let typing = fieldSchema.type;
    if (!Array.isArray(typing)) {
      typing = [typing];
    }

    const nonNullTypes = typing.filter((t) => t !== 'null');
    if (nonNullTypes.length > 1 && !allowFuzzyTypes) {
      throw new Error(
        `Schema for ${streamName} has multiple non-null types for field ${field}: ${nonNullTypes.join(', ')}`
      );
    }
  }
};

const validateFieldAgainstSchema = (streamName, field, value, fieldType) => {
  /*
        Currently only checks primitives
        TODO: Check types within Arrays+Objects 
        (Nested validation should probably be under flag)
    */
  let typing = fieldType.type;
  if (!Array.isArray(typing)) {
    typing = [typing];
  }
  const nonNullType = typing.filter((t) => t !== 'null')[0];
  const isNullable = typing.includes('null');
  if (isNullable && value === null) {
    return;
  }
  if (nonNullType === 'string' && typeof value !== 'string') {
    throw new Error(
      `Record for ${streamName} has invalid type ${typeof value} for field ${field}. Expected type string.`
    );
  }
  if (nonNullType === 'number' && typeof value !== 'number') {
    throw new Error(
      `Record for ${streamName} has invalid type ${typeof value} for field ${field}. Expected type number.`
    );
  }
  if (nonNullType === 'boolean' && typeof value !== 'boolean') {
    throw new Error(
      `Record for ${streamName} has invalid type ${typeof value} for field ${field}. Expected type boolean.`
    );
  }
  if (nonNullType === 'array') {
    if (!Array.isArray(value)) {
      throw new Error(
        `Record for ${streamName} has invalid type ${typeof value} for field ${field}. Expected type array.`
      );
    }
  }
  if (nonNullType === 'object') {
    if (typeof value !== 'object') {
      throw new Error(
        `Record for ${streamName} has invalid type ${typeof value} for field ${field}. Expected type object.`
      );
    }
  }
};

const validateRecordAgainstSchema = (streamName, record, schema) => {
  for (const [field, value] of Object.entries(record)) {
    const fieldType = schema.properties[field];
    if (!fieldType) {
      throw new Error(`Record for ${streamName} has unknown field ${field}`);
    }
    validateFieldAgainstSchema(streamName, field, value, fieldType);
  }
};

const validateSchemaMessage = (streams, obj, allowFuzzyTypes) => {
  const streamName = obj.stream;
  if (!streamName) {
    throw new Error('Schema record is missing stream name');
  }

  if (!obj.schema) {
    throw new Error(`Schema record for ${streamName} is missing schema field`);
  }

  if (!streams[streamName]) {
    streams[streamName] = {
      schema: obj.schema,
      keyProperties: obj.key_properties || [],
      keySet: new Set(),
      count: 0,
    };
  } else {
    for (const keyProperty of obj.key_properties) {
      if (!streams[streamName].keyProperties.includes(keyProperty)) {
        throw new Error(
          `Schema for ${streamName} has different key properties: ${streams[streamName].keyProperties.join(
            ', '
          )} and ${obj.key_properties.join(', ')}`
        );
      }
    }

    const currentSchema = streams[streamName].schema;
    validateSchemaMatching(streamName, obj.schema, currentSchema);
  }

  validateSchemaFormat(streamName, obj.schema, allowFuzzyTypes);
  return streams;
};

const validateRecordMessage = (streams, obj, allowDuplicateRecords) => {
  const streamName = obj.stream;
  const record = obj.record;
  if (!record) {
    throw new Error('Record is missing record field');
  }

  if (typeof record !== 'object') {
    throw new Error(`Record has invalid type ${typeof record} for record field. Expected type object.`);
  }

  if (!streamName) {
    throw new Error('Record record is missing stream name');
  }
  if (!streams[streamName]) {
    throw new Error(`Record was written for stream ${streamName} before schema row was written.`);
  }
  const pks = streams[streamName].keyProperties;

  for (const pk of pks) {
    if (!record[pk]) {
      throw new Error(`Record for ${streamName} is missing key property ${pk}`);
    }
    streams[streamName].keySet.add(record[pk]);
  }
  if (pks.length > 0 && !allowDuplicateRecords) {
    const pkHash = crypto
      .createHash('sha256')
      .update(pks.map((pk) => `${pk}:${record[pk]}`).join(''))
      .digest('hex');
    if (streams[streamName].keySet.has(pkHash)) {
      throw new Error(
        `Duplicate record for ${streamName} with primary keys: ${pks.map((pk) => `${pk}: ${record[pk]}`).join(', ')}`
      );
    }
    streams[streamName].keySet.add(pkHash);
  }

  validateRecordAgainstSchema(streamName, record, streams[streamName].schema);

  streams[streamName].count++;
};

const validateStateMessage = (streams, obj) => {
  if (!obj.value) {
    throw new Error(`State record is missing value`);
  }
  if (typeof obj.value !== 'object') {
    throw new Error(`State record has invalid type ${typeof obj.value} for value. Expected type object.`);
  }
};

const validateSingerData = async (dataFilePath, options) => {
  const streams = {};
  const { allowDuplicateRecords, allowFuzzyTypes } = options;

  for await (const obj of generateJsonLines(dataFilePath)) {
    try {
      if (!obj.type) {
        throw new Error(`Singer row is missing type: ${JSON.stringify(obj)}`);
      }
      if (obj.type == 'SCHEMA') {
        validateSchemaMessage(streams, obj, allowFuzzyTypes);
      } else if (obj.type == 'RECORD') {
        validateRecordMessage(streams, obj, allowDuplicateRecords);
      } else if (obj.type == 'STATE') {
        validateStateMessage(streams, obj);
      }
    } catch (error) {
      if(options.json) {
        error.message =`${error.message}. Error occured on line: ${JSON.stringify(obj)}.`;
      } else {
        console.log(`Error occured on line: ${JSON.stringify(obj)} 
        `);
      }
      throw error;
    }
  }
  return streams;
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { allowDuplicateRecords, allowFuzzyTypes, dataFilePath, json } = argv;
  const options = {
    allowDuplicateRecords,
    allowFuzzyTypes,
    json,
  };

  if (!dataFilePath) {
    throw new Error('Data file path is required');
  }

  if (!fs.existsSync(dataFilePath)) {
    throw new Error(`Data file path ${dataFilePath} does not exist`);
  }

  try {
    const streams = await validateSingerData(dataFilePath, options);
    if (json) {
      console.log(JSON.stringify({
        streams: Object.keys(streams).map((stream) => ({
          stream,
            count: streams[stream].count,
        })),
      }, null, 2));
    } else {
    console.log(`
        
🎉🎉🎉 Singer validation passed 🎉🎉🎉
--------------------------------------

${Object.keys(streams).length} streams validated

Record count by stream:
${Object.keys(streams)
  .map(
    (stream) => `
  ${stream}: ${streams[stream].count}`
  )
  .join('')}

    `);
    }
  } catch (error) {
    if (json) {
      console.log(JSON.stringify({
        error: error.message,
      }, null, 2));
    } else {
    console.log(`

🚨🚨🚨 Singer validation failed 🚨🚨🚨
--------------------------------------

${error.message}

    `);
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
